import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Company,
  LIVE_ATTRIBUTION_WINDOW_LABELS,
  LiveAttributionByLive,
  LiveAttributionStats,
  LiveAttributionWindow,
  LiveAudienceGroup,
  LiveAudienceOrigin,
  LiveAudienceTransaction,
  LiveBaselineSample,
  LiveConversionGroup,
  LiveConversionRates,
  DailyConversionRates,
  OrganicTrafficOrigin,
  LiveOriginRow,
  LiveSalesComparison,
} from '../types'
import { LiveCartAudienceSection } from './LiveCartAudienceSection'
import { LiveMetricsSummary, sumLiveCarts } from './LiveMetricsSummary'
import { LiveReportPreviewModal } from './live-report/LiveReportPreviewModal'

interface LiveStatsCardProps {
  stats: LiveAttributionStats
  selectedCompany: Company | null
  startDate?: string
  endDate?: string
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0)
}

function formatDateHourMinute(dhm: string): string {
  if (!dhm || dhm.length < 12) return dhm
  return `${dhm.slice(6, 8)}/${dhm.slice(4, 6)}/${dhm.slice(0, 4)} ${dhm.slice(8, 10)}:${dhm.slice(10, 12)}`
}

/**
 * Fuso da property do GA4 — o MESMO que o `orderEpochMs` do backend assume
 * (America/Sao_Paulo, UTC-3 fixo) ao converter o `dateHourMinute`. Exibir no
 * fuso do navegador deslocaria os horários de quem abre o painel fora do
 * Brasil, fazendo a faixa comparada parecer outra.
 */
const PROPERTY_TIME_ZONE = 'America/Sao_Paulo'
const TZ_LABEL = 'horário de Brasília'

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short',
      timeZone: PROPERTY_TIME_ZONE,
    })
  } catch {
    return iso
  }
}

/** Só o horário (HH:MM) no fuso da property. */
function formatTimeOfDay(ms: number): string {
  return new Date(ms).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: PROPERTY_TIME_ZONE,
  })
}

/** Duração legível a partir de um intervalo em ms (3h16, 45min). */
function formatDurationMs(ms: number): string {
  const mins = Math.round(ms / 60000)
  if (mins < 60) return `${mins}min`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`
}

/** Só o dia (DD/MM) no fuso da property. */
function formatDayShort(ms: number): string {
  return new Date(ms).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    timeZone: PROPERTY_TIME_ZONE,
  })
}

/** Moeda compacta pro eixo do gráfico: R$ 4,7 mil. */
function formatCurrencyCompact(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value || 0)
}

function formatDurationMin(startIso: string, endIso: string | null): string {
  if (!endIso) return '—'
  const diffMs = new Date(endIso).getTime() - new Date(startIso).getTime()
  if (isNaN(diffMs) || diffMs <= 0) return '—'
  const mins = Math.round(diffMs / 60000)
  if (mins < 60) return `${mins}min`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m > 0 ? `${h}h${m}min` : `${h}h`
}

const COVER_GRADIENTS = [
  'linear-gradient(135deg,#ff7a59,#ec4899)',
  'linear-gradient(135deg,#6d3aff,#3b82f6)',
  'linear-gradient(135deg,#0ea5e9,#06b6a4)',
  'linear-gradient(135deg,#f59e0b,#ef4444)',
]

function liveGradient(liveKey: string): string {
  return COVER_GRADIENTS[liveKey.length % 4]
}

const PRODUCT_COLORS = [
  '#6d3aff', '#0ea5e9', '#ec4899', '#10b981', '#f59e0b',
  '#ef4444', '#8b5cf6', '#06b6a4', '#f97316', '#14b8a6',
]

function productColor(title: string): string {
  let h = 0
  for (let i = 0; i < title.length; i++) h = (h * 31 + title.charCodeAt(i)) >>> 0
  return PRODUCT_COLORS[h % PRODUCT_COLORS.length]
}

function initials(name: string): string {
  const parts = name.trim().split(' ').filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0][0].toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

// ─── Main export ──────────────────────────────────────────────────────────────

/**
 * Card dedicado pra exibir stats de Live Commerce. Vive em paralelo ao
 * `CompanyStatsCard` (que mostra stories+ROI) — live é tratada como um
 * "produto" separado, então não somamos com os números de stories nem
 * exibimos ROI agregado aqui.
 */
export function LiveStatsCard({ stats, selectedCompany, startDate, endDate }: LiveStatsCardProps) {
  const hasByLive = stats.byLive && stats.byLive.length > 0
  const windowLabel = LIVE_ATTRIBUTION_WINDOW_LABELS[stats.window]

  const sorted = useMemo(
    () =>
      hasByLive
        ? [...stats.byLive].sort(
            (a, b) => new Date(b.liveStartedAt).getTime() - new Date(a.liveStartedAt).getTime(),
          )
        : [],
    [stats.byLive, hasByLive],
  )

  const [selectedKey, setSelectedKey] = useState<string | null>(
    sorted.length > 0 ? sorted[0].liveKey : null,
  )

  const selectedLive = useMemo(
    () => sorted.find((l) => l.liveKey === selectedKey) ?? null,
    [sorted, selectedKey],
  )

  if (!hasByLive) {
    return (
      <div className="card" style={{ borderLeft: '4px solid var(--accent-coral, #ff7a59)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
          <h2 style={{ margin: 0 }}>
            Live Commerce
            {selectedCompany && (
              <span style={{ fontWeight: 'normal', color: 'var(--ink-400, #6b7280)', marginLeft: 8 }}>
                · {selectedCompany.title}
              </span>
            )}
          </h2>
          <span style={{ fontSize: 12, color: 'var(--ink-400, #6b7280)' }}>
            Janela <strong>{windowLabel}</strong>
            {startDate && endDate && ` · ${startDate} → ${endDate}`}
          </span>
        </div>
        <GeneralView stats={stats} />
      </div>
    )
  }

  const totalAttributed = stats.liveMetrics?.attributed.orders ?? stats.liveMatched
  const periodCart = sumLiveCarts(sorted.map((l) => l.cartAudience?.summary.liveCart))

  return (
    <div className="card" style={{ borderLeft: '4px solid var(--accent-coral, #ff7a59)', padding: 0, overflow: 'hidden' }}>
      {/* Header strip */}
      <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--ink-100, #e5e7eb)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--accent-coral, #ff7a59)' }}>
            Live Commerce · Análise por transmissão
          </span>
        </div>
        <h1 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {selectedCompany?.title ?? 'Empresa'}
          <span style={{
            background: 'var(--accent-coral, #ff7a59)',
            color: '#fff',
            fontSize: 11,
            fontWeight: 700,
            padding: '2px 8px',
            borderRadius: 20,
            letterSpacing: 0.5,
          }}>
            Live Commerce
          </span>
        </h1>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-400, #6b7280)' }}>
          {sorted.length} live{sorted.length !== 1 ? 's' : ''} no período
          {' · '}{totalAttributed.toLocaleString('pt-BR')} pedido{totalAttributed !== 1 ? 's' : ''} com receita atribuída
          {' · '}janela {windowLabel}
          {startDate && endDate && ` · ${startDate} → ${endDate}`}
        </p>
      </div>

      {/* Métricas oficiais do período + ressalvas (o que os números não garantem) */}
      <LiveMetricsSummary
        metrics={stats.liveMetrics}
        legacy={{ orders: stats.liveMatched, revenue: stats.liveInfluencedRevenue }}
        cart={periodCart}
        caveats={stats.liveCaveats}
        scope="no período"
      />

      {/* Live cards carousel */}
      <LiveCarousel
        lives={sorted}
        selectedKey={selectedKey}
        onSelect={setSelectedKey}
      />

      {/* Live detail */}
      {selectedLive && (
        <LiveDetail live={selectedLive} companyTitle={selectedCompany?.title ?? 'Empresa'} window={stats.window} />
      )}
    </div>
  )
}

// ─── LiveCarousel ─────────────────────────────────────────────────────────────

const CARD_WIDTH = 340
const CARD_GAP = 14
const PEEK = 28 // visible portion of next card

function LiveCarousel({ lives, selectedKey, onSelect }: {
  lives: LiveAttributionByLive[]
  selectedKey: string | null
  onSelect: (key: string) => void
}) {
  const trackRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const checkScroll = () => {
    const el = trackRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 4)
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4)
  }

  useEffect(() => {
    const el = trackRef.current
    if (!el) return
    checkScroll()
    el.addEventListener('scroll', checkScroll, { passive: true })
    const ro = new ResizeObserver(checkScroll)
    ro.observe(el)
    return () => { el.removeEventListener('scroll', checkScroll); ro.disconnect() }
  }, [lives.length])

  const scroll = (dir: 'left' | 'right') => {
    const el = trackRef.current
    if (!el) return
    const step = CARD_WIDTH + CARD_GAP
    el.scrollBy({ left: dir === 'right' ? step : -step, behavior: 'smooth' })
  }

  return (
    <div style={{
      position: 'relative',
      background: 'var(--ink-50, #f9fafb)',
      borderBottom: '1px solid var(--ink-100)',
    }}>
      {/* Left arrow */}
      {canScrollLeft && (
        <button
          onClick={() => scroll('left')}
          aria-label="Anterior"
          style={{
            position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
            zIndex: 2, width: 32, height: 32, borderRadius: '50%',
            background: 'var(--paper)', border: '1px solid var(--ink-200)',
            boxShadow: 'var(--shadow-sm)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--ink-700)',
          }}
        >
          <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6"/>
          </svg>
        </button>
      )}

      {/* Scrollable track */}
      <div
        ref={trackRef}
        className="scroll-hide"
        style={{
          display: 'flex',
          gap: CARD_GAP,
          padding: `16px 20px 16px ${canScrollLeft ? 52 : 20}px`,
          paddingRight: canScrollRight ? PEEK + 20 : 20,
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
        } as React.CSSProperties}
      >
        {lives.map((live) => (
          <div key={live.liveKey} style={{ scrollSnapAlign: 'start', flexShrink: 0, width: CARD_WIDTH }}>
            <LiveCard
              live={live}
              selected={selectedKey === live.liveKey}
              onSelect={() => onSelect(live.liveKey)}
            />
          </div>
        ))}
      </div>

      {/* Right arrow */}
      {canScrollRight && (
        <button
          onClick={() => scroll('right')}
          aria-label="Próximo"
          style={{
            position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
            zIndex: 2, width: 32, height: 32, borderRadius: '50%',
            background: 'var(--paper)', border: '1px solid var(--ink-200)',
            boxShadow: 'var(--shadow-sm)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--ink-700)',
          }}
        >
          <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        </button>
      )}
    </div>
  )
}

// ─── GeneralView (fallback, no byLive data) ───────────────────────────────────

function GeneralView({ stats }: { stats: LiveAttributionStats }) {
  return (
    <div style={{ marginTop: 16, marginLeft: -20, marginRight: -20 }}>
      <LiveMetricsSummary
        metrics={stats.liveMetrics}
        legacy={{ orders: stats.liveMatched, revenue: stats.liveInfluencedRevenue }}
        cart={null}
        caveats={stats.liveCaveats}
        scope="no período"
      />
    </div>
  )
}

// ─── LiveCard ─────────────────────────────────────────────────────────────────

function LiveCard({ live, selected, onSelect }: { live: LiveAttributionByLive; selected: boolean; onSelect: () => void }) {
  const isLive = !live.liveEndedAt
  const duration = formatDurationMin(live.liveStartedAt, live.liveEndedAt)
  const totalAudience = live.audienceTransactions.length
  const matchedAudience = live.audienceTransactions.filter((tx) => tx.matched).length
  const matchPct = totalAudience > 0 ? Math.round((matchedAudience / totalAudience) * 100) : null

  return (
    <div
      onClick={onSelect}
      style={{
        borderRadius: 10,
        overflow: 'hidden',
        border: selected
          ? '2px solid var(--accent-coral, #ff7a59)'
          : '2px solid var(--ink-100, #e5e7eb)',
        boxShadow: selected
          ? '0 4px 16px rgba(255,122,89,0.25)'
          : 'var(--shadow-sm, 0 1px 4px rgba(0,0,0,0.08))',
        cursor: 'pointer',
        background: 'var(--paper, #fff)',
        transition: 'box-shadow 150ms, border-color 150ms',
      }}
    >
      {/* Cover */}
      <div style={{
        height: 88,
        background: liveGradient(live.liveKey),
        position: 'relative',
        padding: '10px 12px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}>
        {/* Top row: status + date */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            background: 'rgba(0,0,0,0.35)',
            color: '#fff',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.8,
            padding: '2px 7px',
            borderRadius: 20,
          }}>
            <span style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: isLive ? '#22c55e' : '#fff',
              display: 'inline-block',
            }} />
            {isLive ? 'EM CURSO' : 'ENCERRADA'}
          </span>
          <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11, fontWeight: 600 }}>
            {formatDateTime(live.liveStartedAt).split(' ')[0]}
          </span>
        </div>

        {/* Title */}
        <div>
          <div style={{
            color: '#fff',
            fontSize: 13,
            fontWeight: 700,
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
          }}>
            {live.liveTitle || <em style={{ fontStyle: 'italic', opacity: 0.7 }}>sem título</em>}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 10, marginTop: 2 }}>
            duração: {duration}
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '12px 14px' }}>
        {/* Mini stats row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
          <MiniStat label="Duração" value={duration} />
          <MiniStat
            label="Match GA"
            value={matchPct !== null ? `${matchPct}%` : '—'}
          />
          <MiniStat label="Catálogo" value={live.products.length.toLocaleString('pt-BR')} />
        </div>

        {/* Revenue */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={{ fontSize: 11, color: 'var(--ink-400, #6b7280)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Receita atribuída
            {live.caveats && live.caveats.length > 0 && (
              <span title="Esta live tem ressalvas" style={{ marginLeft: 6, color: '#b45309' }}>⚠</span>
            )}
          </span>
          <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--accent-coral, #ff7a59)' }}>
            {formatCurrency(live.metrics?.attributed.revenue ?? live.liveInfluencedRevenue)}
          </span>
        </div>
      </div>
    </div>
  )
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-800, #111827)' }}>{value}</div>
      <div style={{ fontSize: 10, color: 'var(--ink-400, #6b7280)', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
    </div>
  )
}

// ─── LiveDetail ───────────────────────────────────────────────────────────────

function LiveDetail({ live, companyTitle, window }: { live: LiveAttributionByLive; companyTitle: string; window: LiveAttributionWindow }) {
  const [reportOpen, setReportOpen] = useState(false)
  return (
    <div style={{ background: 'var(--paper, #fff)' }}>
      {/* Detail header */}
      <div style={{
        padding: '14px 20px',
        borderBottom: '1px solid var(--ink-100, #e5e7eb)',
        background: 'var(--ink-50, #f9fafb)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 16,
        flexWrap: 'wrap',
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--accent-coral, #ff7a59)' }}>
              Live
            </span>
            <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink-800, #111827)' }}>
              {live.liveTitle || 'sem título'}
            </span>
            <code style={{ fontSize: 11, color: 'var(--ink-400, #6b7280)', fontFamily: 'var(--font-mono, monospace)' }}>
              {live.liveKey.slice(0, 12)}…
            </code>
          </div>
          <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--ink-400, #6b7280)', flexWrap: 'wrap' }}>
            <span><strong>Início:</strong> {formatDateTime(live.liveStartedAt)}</span>
            <span><strong>Fim:</strong> {live.liveEndedAt ? formatDateTime(live.liveEndedAt) : <em style={{ color: 'var(--success, #16a34a)' }}>em curso</em>}</span>
            <span>
              <strong>Atribui até:</strong>{' '}
              {formatDateTime(live.effectiveAttributionWindowEnd)}
              {live.effectiveAttributionWindowEnd !== live.attributionWindowEnd && (
                <span title="janela cortada por live consecutiva" style={{ color: 'var(--warning, #d97706)', marginLeft: 4 }}>*</span>
              )}
            </span>
          </div>
        </div>

        {/* O relatório desta live (HTML/PDF) — abre a prévia com a escolha de seções. */}
        <button
          type="button"
          onClick={() => setReportOpen(true)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
            padding: '7px 12px', fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: 'pointer',
            background: 'var(--accent-coral, #ff7a59)', color: '#fff', border: 'none',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Baixar relatório
        </button>
      </div>

      {reportOpen && (
        <LiveReportPreviewModal live={live} companyTitle={companyTitle} window={window} onClose={() => setReportOpen(false)} />
      )}

      {/* Métricas oficiais desta live + ressalvas dela */}
      <LiveMetricsSummary
        metrics={live.metrics}
        legacy={{ orders: live.liveInfluencedMatched, revenue: live.liveInfluencedRevenue }}
        cart={live.cartAudience?.summary.liveCart ?? null}
        caveats={live.caveats}
        scope="nesta live"
      />

      {/* Sales comparison strip */}
      {live.salesComparison && (
        <SalesComparisonStrip
          comparison={live.salesComparison}
          conversion={live.conversion}
          dailyConversion={live.dailyConversion}
          windowStartIso={live.liveStartedAt}
          windowEndIso={live.effectiveAttributionWindowEnd}
        />
      )}

      {/* Buyers split + traffic origin */}
      {live.audienceOrigin && <AudienceOriginSection origin={live.audienceOrigin} />}

      {/* Origem do orgânico — GA4, em sessões (bloco à parte de propósito) */}
      {live.organicOrigin && <OrganicOriginSection origin={live.organicOrigin} />}

      {/* 3-column panel */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1.2fr 1fr', minHeight: 300 }}>
        <div style={{ borderRight: '1px solid var(--ink-100, #e5e7eb)' }}>
          <CatalogPanel live={live} />
        </div>
        <div style={{ borderRight: '1px solid var(--ink-100, #e5e7eb)' }}>
          <OrdersPanel live={live} />
        </div>
        <div>
          <AudiencePanel live={live} />
        </div>
      </div>

      {/* Carrinho do público — todos os espectadores, o que tinham no carrinho
          e o que disso a live converteu (ver LiveCartAudienceSection). */}
      {live.cartAudience && <LiveCartAudienceSection audience={live.cartAudience} />}
    </div>
  )
}

// ─── WindowShareNote ──────────────────────────────────────────────────────────

/** "22/10" a partir de `YYYY-MM-DD`. */
function dayLabel(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  return m ? `${m[3]}/${m[2]}` : iso
}

/**
 * Contexto da janela em relação ao(s) dia(s) que ela toca. Antes, janela maior
 * que o dia da live virava "Números inconsistentes — não use" em vermelho, mas
 * isso só acontecia porque a janela atravessava a meia-noite: o dado estava
 * certo e o aviso tirava a confiança do time nele.
 */
function WindowShareNote({ comparison }: { comparison: LiveSalesComparison }) {
  const muted: React.CSSProperties = { marginTop: 6, fontSize: 11, color: 'var(--ink-400, #9ca3af)' }
  const strong: React.CSSProperties = { color: 'var(--ink-600, #4b5563)' }
  const days = comparison.windowDays

  if (days && days.count > 1) {
    const share = comparison.windowShareOfWindowDaysPct
    return (
      <div style={muted}>
        A janela atravessa <strong style={strong}>{days.count} dias</strong> ({dayLabel(days.firstDay)} a {dayLabel(days.lastDay)}) e
        representa <strong style={strong}>{share !== null && share !== undefined ? `${share.toFixed(0)}%` : '—'}</strong> da
        receita desses dias ({formatCurrency(days.revenue)} em {days.orders.toLocaleString('pt-BR')} pedidos). Só o dia da live
        ({dayLabel(days.firstDay)}) somou {formatCurrency(comparison.day.revenue)} · {TZ_LABEL}.
      </div>
    )
  }

  // Backend anterior (sem `windowDays`): acima de 100% só pode ser janela que
  // atravessa a meia-noite. Explica em vez de acusar erro.
  if (comparison.windowShareOfDayPct !== null && comparison.windowShareOfDayPct > 100.5) {
    return (
      <div style={muted}>
        A janela soma {formatCurrency(comparison.window.revenue)}, mais que o dia da live sozinho
        ({formatCurrency(comparison.day.revenue)}): ela atravessa a meia-noite e inclui vendas do dia seguinte.
      </div>
    )
  }

  return (
    <div style={muted}>
      A janela representa{' '}
      <strong style={strong}>
        {comparison.windowShareOfDayPct !== null ? `${comparison.windowShareOfDayPct.toFixed(0)}%` : '—'}
      </strong>{' '}
      da receita do dia da live ({formatCurrency(comparison.day.revenue)} em {comparison.day.orders.toLocaleString('pt-BR')} pedidos,
      dia civil em {TZ_LABEL}).
    </div>
  )
}

// ─── SalesComparisonStrip ─────────────────────────────────────────────────────

/** Dia sem live: recessivo, é o pano de fundo contra o qual a live é lida. */
const BASELINE_GRAY = '#9ca3af'

/**
 * Leitura em texto da posição da live na distribuição — graduada, porque
 * "bateu 9 de 10" e "bateu 5 de 10" não significam a mesma coisa. Os cortes
 * (80% / 20%) são convenção de leitura declarada, não teste estatístico: com
 * poucos dias de amostra nenhuma inferência formal se sustenta.
 */
function baselineVerdict(daysBelow: number, sampleDays: number): string {
  if (sampleDays === 0) return ''
  if (daysBelow === sampleDays) return 'bateu todos os dias comparáveis'
  if (daysBelow === 0) return 'ficou abaixo de todos — a live não explica as vendas'
  const share = daysBelow / sampleDays
  if (share >= 0.8) return 'acima do padrão dos dias sem live'
  if (share <= 0.2) return 'abaixo do padrão dos dias sem live'
  return 'dentro da variação normal dos dias sem live'
}

/**
 * "A live gerou venda nova?" — a comparação explícita entre a janela da live e
 * a MESMA faixa de horário em dias sem live.
 *
 * O gráfico existe porque a média sozinha não responde a pergunta: +40% sobre a
 * média pode ser um dia normal se os dias sem live variam 3× entre si. Mostrando
 * cada dia comparável, dá pra ver se a live saiu do padrão ou está dentro do
 * ruído — e o veredito ("acima de N de M dias") é contagem, não teste
 * estatístico inventado.
 */
function SalesComparisonStrip({ comparison, conversion, dailyConversion, windowStartIso, windowEndIso }: {
  comparison: LiveSalesComparison
  /** Taxas por grupo na JANELA; ausente quando o alcance não pôde ser lido. */
  conversion?: LiveConversionRates
  /** Taxas no recorte do DIA — o único com o grupo orgânico. */
  dailyConversion?: DailyConversionRates
  /** `liveStartedAt` — início da faixa comparada. */
  windowStartIso: string
  /** `effectiveAttributionWindowEnd` — fim da faixa comparada. */
  windowEndIso: string
}) {
  const { window: win, viewers, identifiedNonViewers, unidentified, baseline, upliftRevenuePct } = comparison
  const hasBaseline = baseline.sampleDays > 0 && baseline.avgRevenue !== null

  const startMs = Date.parse(windowStartIso)
  const endMs = Date.parse(windowEndIso)
  const windowDuration =
    !isNaN(startMs) && !isNaN(endMs) && endMs > startMs ? formatDurationMs(endMs - startMs) : ''

  // Evidência de que a bridge widdeUid→GA4 entregou nesta janela.
  const hasIdentifiedBuyer = viewers.orders > 0 || identifiedNonViewers.orders > 0

  return (
    <div style={{ borderBottom: '1px solid var(--ink-100, #e5e7eb)', padding: '12px 20px 16px' }}>
      {/* A JANELA, declarada — sem isso o leitor não sabe o que está somado */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--ink-500, #6b7280)' }}>
          Impacto da live · vendas da loja inteira
        </span>
        <span style={{ fontSize: 11, color: 'var(--ink-500, #6b7280)' }}>
          janela: <strong>{formatDateTime(windowStartIso)}</strong> → <strong>{formatDateTime(windowEndIso)}</strong>
          {windowDuration && <> · {windowDuration}</>} · {TZ_LABEL}
        </span>
      </div>

      {/* Quanto do(s) dia(s) a janela representa. Contexto, não alerta: janela
          de 24h numa live de fim de tarde atravessa a meia-noite e soma mais
          que o dia da live sozinho — e isso é esperado. */}
      <WindowShareNote comparison={comparison} />

      {hasBaseline ? (
        <BaselineComparison
          windowRevenue={win.revenue}
          windowOrders={win.orders}
          baseline={baseline}
          upliftRevenuePct={upliftRevenuePct}
          windowStartIso={windowStartIso}
          windowEndIso={windowEndIso}
        />
      ) : (
        <div style={{
          marginTop: 10,
          padding: '10px 12px',
          background: 'var(--warning-bg, #fff4e0)',
          border: '1px solid #fde047',
          borderRadius: 8,
          fontSize: 12,
          color: 'var(--ink-600, #4b5563)',
        }}>
          <strong>Sem dias comparáveis no período.</strong> Não há como dizer se a live
          mudou as vendas — todos os dias anteriores dentro do período ou tiveram live,
          ou estão fora do intervalo de pedidos baixado. Amplie o período para trás.
        </div>
      )}

      {/* Composição da janela em TRÊS grupos que fecham com o total. O terceiro
          existe porque a maioria dos pedidos não tem widde_uid: chamá-los de
          "não assistiram" afirmaria o que não sabemos. */}
      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, color: 'var(--ink-400, #9ca3af)', marginBottom: 6 }}>
          Como as {formatCurrency(win.revenue)} da janela se dividem
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
          gap: 10,
        }}>
          <ComparisonStat
            label="Assistiram"
            orders={viewers.orders}
            revenue={viewers.revenue}
            hint="identificados com evento desta live"
            accent={SERIES_VIEWERS}
            conversion={conversion?.liveViewers}
            conversionHint="espectadores da live"
            dayConversion={dailyConversion?.liveViewers}
            dayHint="quem assistiu, no dia inteiro"
          />
          <ComparisonStat
            label="Widde, fora da live"
            orders={identifiedNonViewers.orders}
            revenue={identifiedNonViewers.revenue}
            hint="passou por outra aplicação nossa, não por esta live"
            accent={SERIES_NON_VIEWERS}
            conversion={conversion?.widdeOutsideLive}
            conversionHint="alcançados fora da live"
            dayConversion={dailyConversion?.widdeOutsideLive}
            dayHint="contato Widde fora desta live, no dia"
          />
          <ComparisonStat
            label="Orgânico"
            orders={unidentified.orders}
            revenue={unidentified.revenue}
            hint="sem widde_uid: não passou por nenhuma aplicação Widde"
            accent={BASELINE_GRAY}
            dayConversion={dailyConversion?.organic}
            dayHint={
              dailyConversion
                ? `visitantes do dia sem contato Widde${dailyConversion.organicBuyersSource === 'estimated' ? ' · compradores estimados' : ''}`
                : undefined
            }
            noConversionReason="sem denominador na janela: o GA4 não recorta pessoas únicas por hora"
          />
        </div>

        {dailyConversion && (
          <p style={{ margin: '8px 0 0', fontSize: 10, color: 'var(--ink-300, #9ca3af)', lineHeight: 1.5 }}>
            <strong style={{ color: 'var(--ink-500, #6b7280)' }}>Por que dois recortes:</strong>{' '}
            o GA4 só entrega contagem de pessoas únicas por DIA — o intervalo de datas da API não aceita
            hora, e somar hora a hora contaria de novo quem voltou. Então a comparação entre os três
            grupos é a do dia; a da janela existe só onde o denominador sai do nosso ClickHouse.
            {dailyConversion.organicBuyersSource === 'estimated' &&
              ' Os compradores orgânicos são estimados pela razão pedidos/pessoa dos identificados do mesmo dia — esta property não expõe totalPurchasers.'}
          </p>
        )}

        {dailyConversion?.reachInconsistent && (
          <div style={{
            marginTop: 8,
            padding: '10px 12px',
            background: 'var(--warning-bg, #fff4e0)',
            border: '1px solid #fde047',
            borderRadius: 8,
            fontSize: 12,
            color: 'var(--ink-700, #374151)',
          }}>
            <strong>Taxa orgânica do dia indisponível.</strong> O alcance medido no nosso ClickHouse
            passou do total de visitantes do GA4 — as duas fontes contam &ldquo;pessoa&rdquo; de formas
            diferentes (cookie nosso × identidade do GA4) e aqui discordaram, então a subtração não
            produz um denominador confiável.
          </div>
        )}

        {conversion && conversion.buyersWithPriorContactOnly > 0 && (
          <p style={{ margin: '8px 0 0', fontSize: 10, color: 'var(--ink-300, #9ca3af)', lineHeight: 1.5 }}>
            {conversion.buyersWithPriorContactOnly} comprador
            {conversion.buyersWithPriorContactOnly !== 1 ? 'es' : ''} identificado
            {conversion.buyersWithPriorContactOnly !== 1 ? 's' : ''} por contato ANTERIOR à janela
            {conversion.buyersWithPriorContactOnly !== 1 ? ' ficam' : ' fica'} fora das taxas — sem estar no
            denominador, entrar{conversion.buyersWithPriorContactOnly !== 1 ? 'iam' : 'ia'} só no numerador e
            a taxa passaria de 100%.
          </p>
        )}

        {/* A leitura "orgânico" depende da bridge widdeUid→GA4 estar entregando.
            Sem NENHUM comprador identificado não há como distinguir tráfego
            orgânico de ponte quebrada — e aí o grupo mede a segunda coisa. */}
        {hasIdentifiedBuyer ? (
          <p style={{ margin: '8px 0 0', fontSize: 10, color: 'var(--ink-300, #9ca3af)', lineHeight: 1.5 }}>
            O <code style={{ fontFamily: 'var(--font-mono, monospace)' }}>widde_uid</code> só é gravado
            quando há contato com uma aplicação Widde (≥2s engajado na live, ou view de vídeo), então
            pedido sem uid é compra orgânica.
          </p>
        ) : (
          <div style={{
            marginTop: 8,
            padding: '10px 12px',
            background: 'var(--warning-bg, #fff4e0)',
            border: '1px solid #fde047',
            borderRadius: 8,
            fontSize: 12,
            color: 'var(--ink-700, #374151)',
          }}>
            <strong>Nenhum comprador identificado nesta janela.</strong> Sem isso não dá pra afirmar
            que os {unidentified.orders.toLocaleString('pt-BR')} pedidos são orgânicos: pode ser a bridge{' '}
            <code style={{ fontFamily: 'var(--font-mono, monospace)' }}>widdeUid</code> → GA4 sem entregar
            (loja sem GA4/GTM configurado, ou a user property não criada na conta).
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Cabeçalho de comparação + distribuição dos dias sem live.
 *
 * Uma medida só (receita) num eixo — pedidos vão pro tooltip. Nunca dois eixos.
 */
function BaselineComparison({ windowRevenue, windowOrders, baseline, upliftRevenuePct, windowStartIso, windowEndIso }: {
  windowRevenue: number
  windowOrders: number
  baseline: LiveSalesComparison['baseline']
  upliftRevenuePct: number | null
  windowStartIso: string
  windowEndIso: string
}) {
  const samples = baseline.samples
  const avg = baseline.avgRevenue ?? 0
  const daysBelow = baseline.daysBelowLive ?? 0
  const isUp = (upliftRevenuePct ?? 0) >= 0

  // Escala com 15% de folga no topo pro rótulo da coluna mais alta caber sem
  // encostar na borda do gráfico.
  const max = Math.max(windowRevenue, baseline.maxRevenue ?? 0, avg, 1) * 1.15

  // A faixa comparada, no fuso da property — responde "que horário exatamente?".
  const startMs = Date.parse(windowStartIso)
  const endMs = Date.parse(windowEndIso)
  const timeRange = !isNaN(startMs) && !isNaN(endMs)
    ? `${formatTimeOfDay(startMs)}–${formatTimeOfDay(endMs)}`
    : ''

  const deltaColor = isUp ? 'var(--success, #16a34a)' : 'var(--danger, #dc2626)'

  return (
    <div style={{
      marginTop: 10,
      border: '1px solid var(--ink-100, #e5e7eb)',
      borderRadius: 10,
      overflow: 'hidden',
    }}>
      {/* Frase de comparação + delta */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 20,
        flexWrap: 'wrap',
        padding: '14px 16px',
        background: isUp ? 'var(--success-bg, #f0fdf4)' : 'var(--ink-50, #f9fafb)',
        borderBottom: '1px solid var(--ink-100, #e5e7eb)',
      }}>
        <div>
          <div style={{ fontSize: 32, fontWeight: 800, color: deltaColor, lineHeight: 1 }}>
            {upliftRevenuePct !== null
              ? `${isUp ? '+' : ''}${upliftRevenuePct.toFixed(0)}%`
              : '—'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-500, #6b7280)', marginTop: 3 }}>
            receita vs dias sem live
          </div>
        </div>

        {/* As duas pontas da conta, lado a lado, com swatch de identidade */}
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
          <SideBySide
            color={SERIES_VIEWERS}
            label="Na janela da live"
            value={formatCurrency(windowRevenue)}
            sub={`${windowOrders.toLocaleString('pt-BR')} pedido${windowOrders !== 1 ? 's' : ''}`}
          />
          <SideBySide
            color={BASELINE_GRAY}
            label={`Média de ${baseline.sampleDays} dia${baseline.sampleDays !== 1 ? 's' : ''} sem live`}
            value={formatCurrency(avg)}
            sub={
              baseline.minRevenue !== null && baseline.maxRevenue !== null
                ? `varia de ${formatCurrencyCompact(baseline.minRevenue)} a ${formatCurrencyCompact(baseline.maxRevenue)}`
                : ''
            }
          />
        </div>

        {/* Veredito por contagem — não é teste estatístico. Sem `margin-left:
            auto`: ao quebrar linha em tela estreita, ele alinha à esquerda em
            vez de deixar um vão. */}
        <div style={{
          background: 'var(--paper, #fff)',
          border: '1px solid var(--ink-200, #e5e7eb)',
          borderRadius: 8,
          padding: '8px 12px',
          maxWidth: 230,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-800, #111827)' }}>
            Vendeu mais que {daysBelow} de {baseline.sampleDays} dia{baseline.sampleDays !== 1 ? 's' : ''}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-500, #6b7280)', marginTop: 2 }}>
            {baselineVerdict(daysBelow, baseline.sampleDays)}
          </div>
        </div>
      </div>

      {/* Distribuição: um dia por coluna, a live destacada no fim */}
      <BaselineDistributionChart
        samples={samples}
        windowRevenue={windowRevenue}
        windowOrders={windowOrders}
        avg={avg}
        max={max}
      />

      <div style={{ padding: '0 16px 12px', fontSize: 10, color: 'var(--ink-300, #9ca3af)', lineHeight: 1.5 }}>
        Cada coluna é a MESMA faixa de horário da live{timeRange && ` (${timeRange})`} num dia anterior sem live,
        em {TZ_LABEL}. Dias com qualquer live na faixa ficam de fora do baseline.
      </div>
    </div>
  )
}

function SideBySide({ color, label, value, sub }: { color: string; label: string; value: string; sub: string }) {
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <span style={{ width: 3, borderRadius: 2, background: color, flexShrink: 0 }} />
      <div>
        <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600, color: 'var(--ink-400, #6b7280)' }}>
          {label}
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink-800, #111827)', lineHeight: 1.25 }}>
          {value}
        </div>
        {sub && <div style={{ fontSize: 11, color: 'var(--ink-500, #6b7280)' }}>{sub}</div>}
      </div>
    </div>
  )
}

const CHART_HEIGHT = 96

/**
 * Colunas: cada dia sem live em cinza recessivo, a janela da live em coral, com
 * a média como hairline sólida de referência. Topo arredondado em 4px (a ponta
 * do dado), base reta no baseline; gap de 2px entre colunas adjacentes.
 *
 * Rótulo direto só na coluna da live e na média — número em toda coluna viraria
 * ruído; o resto está no tooltip de cada coluna.
 */
function BaselineDistributionChart({ samples, windowRevenue, windowOrders, avg, max }: {
  samples: LiveBaselineSample[]
  windowRevenue: number
  windowOrders: number
  avg: number
  max: number
}) {
  const avgPct = (avg / max) * 100
  const LIVE_COL_WIDTH = 46

  // Barras e rótulos vivem em faixas separadas com a MESMA geometria de flex —
  // empilhá-los na mesma coluna fazia a barra alta empurrar o rótulo pra fora,
  // desalinhando a linha de datas.
  const trackStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-end',
    gap: SURFACE_GAP_PX,
  }
  const separator = (
    <div style={{ width: 1, flex: '0 0 1px', margin: `0 ${SURFACE_GAP_PX}px` }} />
  )

  return (
    <div style={{ padding: '10px 16px 8px' }}>
      {/* Rótulo da média fora da área das colunas, à esquerda — nunca colide
          com o rótulo de valor da coluna mais alta, que fica à direita. */}
      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-500, #6b7280)', marginBottom: 4 }}>
        <span style={{ display: 'inline-block', width: 14, height: 1, background: BASELINE_GRAY, verticalAlign: 'middle', marginRight: 5 }} />
        média dos dias sem live · {formatCurrencyCompact(avg)}
      </div>

      <div style={{ position: 'relative', height: CHART_HEIGHT }}>
        {/* Linha de média — referência hairline sólida, atrás das colunas */}
        <div style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: `${avgPct}%`,
          height: 1,
          background: BASELINE_GRAY,
          zIndex: 1,
        }} />

        <div style={{ ...trackStyle, height: '100%', position: 'relative', zIndex: 2 }}>
          {samples.map((s) => (
            <DayBar
              key={s.startMs}
              heightPct={(s.revenue / max) * 100}
              color={BASELINE_GRAY}
              tooltip={`${formatDayShort(s.startMs)} · ${formatTimeOfDay(s.startMs)}\n${s.orders} pedido${s.orders !== 1 ? 's' : ''} · ${formatCurrency(s.revenue)}`}
            />
          ))}
          {/* Separador: a live não é "mais um dia" da distribuição */}
          <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--ink-200, #e5e7eb)', margin: `0 ${SURFACE_GAP_PX}px`, flex: '0 0 1px' }} />
          <DayBar
            heightPct={(windowRevenue / max) * 100}
            color={SERIES_VIEWERS}
            width={LIVE_COL_WIDTH}
            valueLabel={formatCurrencyCompact(windowRevenue)}
            tooltip={`Janela da live\n${windowOrders} pedido${windowOrders !== 1 ? 's' : ''} · ${formatCurrency(windowRevenue)}`}
          />
        </div>
      </div>

      {/* Faixa de rótulos — mesma geometria, alinhada por construção */}
      <div style={{ ...trackStyle, marginTop: 5 }}>
        {samples.map((s) => (
          <AxisLabel key={s.startMs}>{formatDayShort(s.startMs)}</AxisLabel>
        ))}
        {separator}
        <AxisLabel width={LIVE_COL_WIDTH} emphasized>LIVE</AxisLabel>
      </div>
    </div>
  )
}

function DayBar({ heightPct, color, tooltip, width, valueLabel }: {
  heightPct: number
  color: string
  tooltip: string
  width?: number
  valueLabel?: string
}) {
  return (
    <div
      title={tooltip}
      style={{
        flex: width ? `0 0 ${width}px` : 1,
        minWidth: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        alignItems: 'center',
        cursor: 'default',
      }}
    >
      {valueLabel && (
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-800, #111827)', marginBottom: 3, whiteSpace: 'nowrap' }}>
          {valueLabel}
        </div>
      )}
      <div style={{
        width: '100%',
        maxWidth: 24,
        // Piso de 2px pra que um dia sem venda continue visível como coluna zerada
        height: `${Math.max(2, heightPct)}%`,
        background: color,
        borderRadius: '4px 4px 0 0',
      }} />
    </div>
  )
}

function AxisLabel({ children, width, emphasized }: {
  children: React.ReactNode
  width?: number
  emphasized?: boolean
}) {
  return (
    <div style={{
      flex: width ? `0 0 ${width}px` : 1,
      minWidth: 0,
      textAlign: 'center',
      fontSize: 9,
      fontWeight: emphasized ? 700 : 400,
      color: emphasized ? 'var(--ink-700, #374151)' : 'var(--ink-400, #9ca3af)',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }}>
      {children}
    </div>
  )
}

/**
 * Uma linha de taxa, com o recorte declarado. Os dois recortes convivem porque
 * medem coisas diferentes: a janela responde "quem estava na live comprou?" e o
 * dia é o único em que o grupo orgânico tem denominador — logo, o único que
 * permite comparar as três colunas.
 *
 * `ratePct === null` vira "—", nunca 0%: sem denominador o grupo não foi
 * medido, e 0% leria como "ninguém converteu".
 */
function RateLine({ scope, group, hint, emphasis, broken }: {
  scope: string
  group: LiveConversionGroup
  hint?: string
  emphasis?: boolean
  broken?: boolean
}) {
  return (
    <div style={{ marginTop: 3 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: emphasis ? 18 : 13,
          fontWeight: emphasis ? 800 : 700,
          lineHeight: 1.2,
          color: broken ? 'var(--danger, #dc2626)' : 'var(--ink-800, #111827)',
        }}>
          {group.ratePct !== null ? `${group.ratePct.toFixed(1).replace('.', ',')}%` : '—'}
        </span>
        <span style={{ fontSize: 10, color: 'var(--ink-400, #9ca3af)' }}>{scope}</span>
        <span style={{ fontSize: 11, color: 'var(--ink-500, #6b7280)' }}>
          {group.buyers.toLocaleString('pt-BR')} de {group.reach.toLocaleString('pt-BR')}
        </span>
      </div>
      {hint && <div style={{ fontSize: 10, color: 'var(--ink-300, #9ca3af)' }}>{hint}</div>}
    </div>
  )
}

/**
 * Card de um grupo. A taxa de conversão vem junto da receita de propósito: é a
 * comparação entre grupos que o leitor procura, e separá-la em outra seção
 * obrigaria a cruzar dois blocos de olho.
 */
function ComparisonStat({ label, orders, revenue, hint, accent, conversion, conversionHint, dayConversion, dayHint, noConversionReason }: {
  label: string
  orders: number | null
  revenue: number | null
  hint: string
  accent?: string
  /** Taxa no recorte da JANELA — só para grupos identificados. */
  conversion?: LiveConversionGroup
  /** O que o denominador da janela significa ("espectadores da live"). */
  conversionHint?: string
  /** Taxa no recorte do DIA — existe para os três grupos. */
  dayConversion?: LiveConversionGroup
  dayHint?: string
  /** Por que este grupo não tem taxa nenhuma. */
  noConversionReason?: string
}) {
  const ratePct = conversion?.ratePct ?? null
  // Acima de 100% a taxa é impossível: numerador e denominador vêm de leituras
  // diferentes (GA4 × ClickHouse) e discordaram. Melhor avisar do que exibir.
  const rateBroken = ratePct !== null && ratePct > 100

  return (
    <div style={{
      background: 'var(--ink-50, #f9fafb)',
      border: '1px solid var(--ink-100, #e5e7eb)',
      borderLeft: accent ? `3px solid ${accent}` : '1px solid var(--ink-100, #e5e7eb)',
      borderRadius: 8,
      padding: '8px 12px',
    }}>
      <div style={{ fontSize: 10, color: 'var(--ink-400, #6b7280)', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
        {label}
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink-800, #111827)', lineHeight: 1.3 }}>
        {revenue !== null ? formatCurrency(revenue) : '—'}
      </div>
      <div style={{ fontSize: 11, color: 'var(--ink-500, #6b7280)' }}>
        {orders !== null
          ? `${Number.isInteger(orders) ? orders.toLocaleString('pt-BR') : orders.toFixed(1)} pedido${orders === 1 ? '' : 's'}`
          : '—'}
      </div>
      <div style={{ fontSize: 10, color: 'var(--ink-300, #9ca3af)', marginTop: 2 }}>{hint}</div>

      {(conversion || dayConversion || noConversionReason) && (
        <div style={{ marginTop: 8, paddingTop: 7, borderTop: '1px solid var(--ink-100, #f1f3f7)' }}>
          <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600, color: 'var(--ink-400, #9ca3af)' }}>
            Conversão
          </div>

          {/* Recorte do DIA em destaque: é o único que existe para os três
              grupos, então é o que permite comparar as colunas entre si. */}
          {dayConversion ? (
            <RateLine
              scope="no dia"
              group={dayConversion}
              emphasis
              hint={dayHint}
            />
          ) : noConversionReason ? (
            <div style={{ fontSize: 11, color: 'var(--ink-400, #9ca3af)', lineHeight: 1.4, marginTop: 2 }}>
              — <span style={{ fontSize: 10 }}>{noConversionReason}</span>
            </div>
          ) : null}

          {/* Recorte da JANELA: só existe para os grupos identificados. */}
          {conversion && (
            <RateLine
              scope="na janela"
              group={conversion}
              hint={rateBroken ? 'numerador e denominador discordam' : conversionHint}
              broken={rateBroken}
            />
          )}
        </div>
      )}
    </div>
  )
}

// ─── AudienceOriginSection ────────────────────────────────────────────────────

/**
 * Paleta de 2 séries validada (dataviz: banda de luminosidade, piso de croma,
 * separação CVD ΔE 23,0 protan / 31,2 tritan, visão normal ΔE 34,8).
 * O coral fica abaixo de 3:1 na superfície clara, então a regra de relevo se
 * aplica: TODA barra carrega rótulo visível — nunca só a cor.
 */
const SERIES_VIEWERS = '#ff7a59'
const SERIES_NON_VIEWERS = '#2a78d6'

/** Gap na cor da superfície entre segmentos que se tocam (spec de marcas). */
const SURFACE_GAP_PX = 2

function formatPct(part: number, total: number): string {
  if (total <= 0) return '—'
  return `${Math.round((part / total) * 1000) / 10}%`
}

/** `referral:instagram.com` → `instagram.com`; mantém o resto como veio. */
function originLabel(row: LiveOriginRow): string {
  return row.source.startsWith('referral:') ? row.source.slice('referral:'.length) : row.source || '—'
}

/** Detalhe completo pro tooltip — campos que não cabem na linha. */
function originTooltip(row: LiveOriginRow): string {
  const parts = [
    `origem: ${row.source || '—'}`,
    row.medium && `medium: ${row.medium}`,
    row.campaign && `campanha: ${row.campaign}`,
    row.content && `conteúdo: ${row.content}`,
    `${row.buyers} pessoa${row.buyers !== 1 ? 's' : ''} · ${row.orders} pedido${row.orders !== 1 ? 's' : ''}`,
    `receita: ${formatCurrency(row.revenue)}`,
  ].filter(Boolean)
  return parts.join('\n')
}

/**
 * "Quem comprou na janela da live?" — divide os compradores entre quem assistiu
 * e quem não assistiu (PESSOAS, não pedidos) e mostra de onde cada grupo veio.
 *
 * Deliberadamente NÃO exibe taxa de conversão: o denominador de visitantes não
 * existe nesses dados, e dividir compradores por espectadores daria um número
 * que parece taxa sem ser.
 */
function AudienceOriginSection({ origin }: { origin: LiveAudienceOrigin }) {
  const { viewers, nonViewers } = origin
  const totalBuyers = viewers.buyers + nonViewers.buyers

  return (
    <div style={{ borderBottom: '1px solid var(--ink-100, #e5e7eb)', padding: '14px 20px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--ink-500, #6b7280)' }}>
          Compradores identificados · de onde vieram
        </span>
        <span style={{ fontSize: 11, color: 'var(--ink-400, #9ca3af)' }}>
          {totalBuyers.toLocaleString('pt-BR')} pessoa{totalBuyers !== 1 ? 's' : ''} com widde_uid
        </span>
      </div>
      <div style={{ fontSize: 11, color: 'var(--ink-400, #9ca3af)', marginTop: 3 }}>
        Conta PESSOAS, não pedidos, e só as identificadas — por isso os números aqui são
        menores que os pedidos da janela acima.
      </div>

      {totalBuyers === 0 ? (
        <div style={{ padding: '14px 0 4px', fontSize: 12, color: 'var(--ink-400, #9ca3af)' }}>
          Nenhum comprador identificado na janela desta live.
        </div>
      ) : (
        <>
          {/* Os dois totais em destaque — a leitura principal da seção */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: 12,
            marginTop: 12,
          }}>
            <GroupTotalCard
              color={SERIES_VIEWERS}
              label="Assistiu e comprou"
              group={viewers}
              pct={formatPct(viewers.buyers, totalBuyers)}
            />
            <GroupTotalCard
              color={SERIES_NON_VIEWERS}
              label="Comprou sem assistir"
              group={nonViewers}
              pct={formatPct(nonViewers.buyers, totalBuyers)}
            />
          </div>

          <BuyersSplitBar viewers={viewers.buyers} nonViewers={nonViewers.buyers} />

          {/* Ranking de origem por grupo */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 20,
            marginTop: 16,
          }}>
            <OriginRanking
              title="Origem de quem assistiu"
              rows={viewers.byOrigin}
              color={SERIES_VIEWERS}
              groupBuyers={viewers.buyers}
            />
            <OriginRanking
              title="Origem de quem não assistiu"
              rows={nonViewers.byOrigin}
              color={SERIES_NON_VIEWERS}
              groupBuyers={nonViewers.buyers}
            />
          </div>

          <p style={{ margin: '14px 0 0', fontSize: 10, color: 'var(--ink-300, #9ca3af)', lineHeight: 1.5 }}>
            Só compradores com <code style={{ fontFamily: 'var(--font-mono, monospace)' }}>widde_uid</code> no GA4.
            Origem por <em>last non-direct touch</em> antes da compra (UTM, ou domínio do referrer).
            &ldquo;Sem assistir&rdquo; = sem evento de presença desta live na janela, então é um teto:
            espectador cujo tracking falhou cai nesse grupo.
          </p>
        </>
      )}
    </div>
  )
}

/**
 * Proporção de compradores entre os dois grupos. Barra única de 100% com dois
 * segmentos separados por gap na cor da superfície; ponta externa arredondada,
 * junção reta. Rótulo dentro do segmento só quando o texto caber com folga
 * (>= 15% da largura) — nunca clipado.
 */
function BuyersSplitBar({ viewers, nonViewers }: { viewers: number; nonViewers: number }) {
  const total = viewers + nonViewers
  if (total <= 0) return null

  const viewerPct = (viewers / total) * 100
  const nonViewerPct = 100 - viewerPct
  const FIT_THRESHOLD = 15

  return (
    <div
      role="img"
      aria-label={`${viewers} compradores assistiram, ${nonViewers} não assistiram`}
      style={{ display: 'flex', height: 22, marginTop: 12, gap: SURFACE_GAP_PX }}
    >
      {viewers > 0 && (
        <div style={{
          width: `${viewerPct}%`,
          background: SERIES_VIEWERS,
          borderRadius: nonViewers > 0 ? '4px 0 0 4px' : 4,
          display: 'flex',
          alignItems: 'center',
          paddingLeft: 8,
          boxSizing: 'border-box',
        }}>
          {viewerPct >= FIT_THRESHOLD && (
            <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>
              {viewers.toLocaleString('pt-BR')} · {Math.round(viewerPct)}%
            </span>
          )}
        </div>
      )}
      {nonViewers > 0 && (
        <div style={{
          width: `${nonViewerPct}%`,
          background: SERIES_NON_VIEWERS,
          borderRadius: viewers > 0 ? '0 4px 4px 0' : 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          paddingRight: 8,
          boxSizing: 'border-box',
        }}>
          {nonViewerPct >= FIT_THRESHOLD && (
            <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>
              {nonViewers.toLocaleString('pt-BR')} · {Math.round(nonViewerPct)}%
            </span>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Total de um grupo com o número de PESSOAS em destaque — é o que o leitor
 * procura primeiro ("quantos compraram sem assistir?"). Receita, pedidos e
 * ticket médio ficam logo abaixo, na mesma leitura.
 *
 * O ticket médio é derivado aqui (receita ÷ pedidos) e sai `—` sem pedido —
 * nunca R$ 0,00, que leria como "ticket zero" em vez de "não há base".
 */
function GroupTotalCard({ color, label, group, pct }: {
  color: string
  label: string
  group: LiveAudienceGroup
  pct: string
}) {
  const avgTicket = group.orders > 0 ? group.revenue / group.orders : null
  // Receita por pessoa só informa algo quando alguém comprou mais de uma vez —
  // com 1 pedido por pessoa ela é idêntica ao ticket médio, e repetir o mesmo
  // número em duas colunas vizinhas só ocupa espaço.
  const revenuePerPerson =
    group.buyers > 0 && group.orders !== group.buyers ? group.revenue / group.buyers : null

  return (
    <div style={{
      background: 'var(--paper, #fff)',
      border: '1px solid var(--ink-200, #e5e7eb)',
      borderLeft: `3px solid ${color}`,
      borderRadius: 10,
      padding: '12px 14px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <span style={{ width: 9, height: 9, borderRadius: 3, background: color, flexShrink: 0 }} />
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--ink-500, #6b7280)' }}>
          {label}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 6 }}>
        <span style={{ fontSize: 30, fontWeight: 800, color: 'var(--ink-800, #111827)', lineHeight: 1 }}>
          {group.buyers.toLocaleString('pt-BR')}
        </span>
        <span style={{ fontSize: 13, color: 'var(--ink-500, #6b7280)' }}>
          pessoa{group.buyers !== 1 ? 's' : ''} · {pct}
        </span>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 8,
        marginTop: 10,
        paddingTop: 9,
        borderTop: '1px solid var(--ink-100, #f1f3f7)',
      }}>
        <MicroStat label="Receita" value={formatCurrency(group.revenue)} />
        <MicroStat
          label="Pedidos"
          value={group.orders.toLocaleString('pt-BR')}
          sub={revenuePerPerson !== null ? `${formatCurrencyCompact(revenuePerPerson)}/pessoa` : undefined}
        />
        <MicroStat label="Ticket médio" value={avgTicket !== null ? formatCurrency(avgTicket) : '—'} />
      </div>
    </div>
  )
}

function MicroStat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div>
      <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600, color: 'var(--ink-400, #9ca3af)' }}>
        {label}
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-800, #111827)', lineHeight: 1.3 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 10, color: 'var(--ink-400, #9ca3af)' }}>{sub}</div>}
    </div>
  )
}

const ORIGIN_TOP_N = 6

/**
 * Ranking de origens do grupo. Barras horizontais finas (10px), comprimento
 * proporcional ao maior valor do grupo, ponta arredondada em 4px, valor no tip.
 * Um hue só: a identidade do GRUPO já é a cor; a magnitude é o comprimento.
 * Cauda além do top-N é somada numa linha "Outras" — hue nunca é gerado.
 */
function OriginRanking({ title, rows, color, groupBuyers }: {
  title: string
  rows: LiveOriginRow[]
  color: string
  groupBuyers: number
}) {
  const top = rows.slice(0, ORIGIN_TOP_N)
  const tail = rows.slice(ORIGIN_TOP_N)
  const tailBuyers = tail.reduce((s, r) => s + r.buyers, 0)
  const tailRevenue = tail.reduce((s, r) => s + r.revenue, 0)
  const maxBuyers = Math.max(1, ...rows.map((r) => r.buyers))

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
        <span style={{ width: 8, height: 8, borderRadius: 2, background: color, flexShrink: 0 }} />
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--ink-500, #6b7280)' }}>
          {title}
        </span>
      </div>

      {rows.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--ink-300, #9ca3af)' }}>
          Nenhum comprador neste grupo.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          {top.map((row, i) => (
            <OriginBar
              key={`${row.source}-${row.campaign}-${row.content}-${i}`}
              label={originLabel(row)}
              sublabel={row.campaign || row.medium || ''}
              buyers={row.buyers}
              revenue={row.revenue}
              pct={formatPct(row.buyers, groupBuyers)}
              widthPct={(row.buyers / maxBuyers) * 100}
              color={color}
              tooltip={originTooltip(row)}
            />
          ))}
          {tail.length > 0 && (
            <OriginBar
              label={`Outras (${tail.length})`}
              sublabel=""
              buyers={tailBuyers}
              revenue={tailRevenue}
              pct={formatPct(tailBuyers, groupBuyers)}
              widthPct={(tailBuyers / maxBuyers) * 100}
              color={color}
              tooltip={tail.map((r) => `${originLabel(r)}: ${r.buyers}`).join('\n')}
              muted
            />
          )}
        </div>
      )}
    </div>
  )
}

function OriginBar({ label, sublabel, buyers, revenue, pct, widthPct, color, tooltip, muted }: {
  label: string
  sublabel: string
  buyers: number
  revenue: number
  pct: string
  widthPct: number
  color: string
  tooltip: string
  muted?: boolean
}) {
  return (
    <div title={tooltip} style={{ cursor: 'default' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 3 }}>
        <span style={{
          fontSize: 12,
          color: 'var(--ink-700, #374151)',
          fontWeight: 600,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
        }}>
          {label}
          {sublabel && (
            <span style={{ fontWeight: 400, color: 'var(--ink-400, #9ca3af)' }}> · {sublabel}</span>
          )}
        </span>
        {/* Rótulo direto obrigatório: o coral não atinge 3:1 na superfície clara */}
        <span style={{ fontSize: 11, color: 'var(--ink-500, #6b7280)', whiteSpace: 'nowrap', flexShrink: 0 }}>
          <strong style={{ color: 'var(--ink-800, #111827)' }}>{buyers.toLocaleString('pt-BR')}</strong>
          {' '}({pct}) · {formatCurrency(revenue)}
        </span>
      </div>
      <div style={{ height: 10, background: 'var(--ink-50, #f3f4f6)', borderRadius: 4 }}>
        <div style={{
          width: `${Math.max(2, widthPct)}%`,
          height: '100%',
          background: color,
          opacity: muted ? 0.45 : 1,
          borderRadius: 4,
        }} />
      </div>
    </div>
  )
}

// ─── OrganicOriginSection ─────────────────────────────────────────────────────

const ORGANIC_ORIGIN_TOP_N = 8

/**
 * Origem do tráfego orgânico, direto do GA4. Mora em bloco separado do ranking
 * dos compradores identificados de propósito: aquele conta PESSOAS a partir dos
 * nossos eventos, este conta SESSÕES a partir do GA4. Misturar os dois numa
 * régua só faria a soma parecer comparável quando não é.
 *
 * A taxa por linha é pedidos ÷ sessões — a conversão clássica de e-commerce,
 * internamente coerente (as duas métricas são de sessão e ambas somam).
 */
function OrganicOriginSection({ origin }: { origin: OrganicTrafficOrigin }) {
  const rows = origin.rows.filter((r) => r.sessions > 0)
  const top = rows.slice(0, ORGANIC_ORIGIN_TOP_N)
  const maxSessions = Math.max(1, ...rows.map((r) => r.sessions))
  const totalSessions = rows.reduce((s, r) => s + r.sessions, 0)

  return (
    <div style={{ borderBottom: '1px solid var(--ink-100, #e5e7eb)', padding: '14px 20px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--ink-500, #6b7280)' }}>
          {origin.scope === 'organic' ? 'Origem do tráfego orgânico' : 'Origem do tráfego da loja'} · GA4, dia da live
        </span>
        <span style={{ fontSize: 11, color: 'var(--ink-400, #9ca3af)' }}>
          {totalSessions.toLocaleString('pt-BR')} sessões
        </span>
      </div>

      {origin.scope === 'all-store' ? (
        <div style={{
          marginTop: 8,
          padding: '10px 12px',
          background: 'var(--warning-bg, #fff4e0)',
          border: '1px solid #fde047',
          borderRadius: 8,
          fontSize: 12,
          color: 'var(--ink-700, #374151)',
        }}>
          <strong>Estas linhas são da loja inteira, não só do orgânico.</strong> O GA4 desta property
          não aceitou o filtro pela dimensão <code style={{ fontFamily: 'var(--font-mono, monospace)' }}>widdeUid</code>{' '}
          — provavelmente ela não está registrada na conta. Quem passou por uma aplicação nossa está
          incluído aqui.
        </div>
      ) : (
        <div style={{ fontSize: 11, color: 'var(--ink-400, #9ca3af)', marginTop: 3 }}>
          Só quem chegou sem <code style={{ fontFamily: 'var(--font-mono, monospace)' }}>widde_uid</code>{' '}
          (filtrado no próprio GA4). Conta SESSÕES — a origem é atributo de sessão, então as linhas somam;
          os grupos acima contam pessoas, e os dois recortes não se somam.
        </div>
      )}

      {top.length === 0 ? (
        <div style={{ marginTop: 10, fontSize: 12, color: 'var(--ink-300, #9ca3af)' }}>
          Nenhuma sessão retornada pelo GA4 para este dia.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 12 }}>
          {top.map((row, i) => {
            const convPct = row.sessions > 0 ? (row.transactions / row.sessions) * 100 : null
            const label = [row.source || '(sem origem)', row.medium].filter(Boolean).join(' / ')
            return (
              <div key={`${row.source}-${row.medium}-${row.campaign}-${i}`} title={row.campaign ? `campanha: ${row.campaign}` : undefined}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 3 }}>
                  <span style={{
                    fontSize: 12,
                    color: 'var(--ink-700, #374151)',
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                  }}>
                    {label}
                    {row.campaign && row.campaign !== '(not set)' && (
                      <span style={{ fontWeight: 400, color: 'var(--ink-400, #9ca3af)' }}> · {row.campaign}</span>
                    )}
                  </span>
                  {/* Rótulo direto em toda linha: o cinza é recessivo e a barra
                      sozinha não carrega o valor. */}
                  <span style={{ fontSize: 11, color: 'var(--ink-500, #6b7280)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    <strong style={{ color: 'var(--ink-800, #111827)' }}>{row.sessions.toLocaleString('pt-BR')}</strong>
                    {' sessões · '}
                    {row.transactions.toLocaleString('pt-BR')} pedido{row.transactions !== 1 ? 's' : ''}
                    {convPct !== null && ` (${convPct.toFixed(1).replace('.', ',')}%)`}
                    {' · '}{formatCurrency(row.revenue)}
                  </span>
                </div>
                <div style={{ height: 10, background: 'var(--ink-50, #f3f4f6)', borderRadius: 4 }}>
                  <div style={{
                    width: `${Math.max(2, (row.sessions / maxSessions) * 100)}%`,
                    height: '100%',
                    background: BASELINE_GRAY,
                    borderRadius: 4,
                  }} />
                </div>
              </div>
            )
          })}
          {rows.length > top.length && (
            <div style={{ fontSize: 10, color: 'var(--ink-300, #9ca3af)' }}>
              + {rows.length - top.length} origem{rows.length - top.length !== 1 ? 'ns' : ''} fora do top {ORGANIC_ORIGIN_TOP_N}.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── CatalogPanel ─────────────────────────────────────────────────────────────

function CatalogPanel({ live }: { live: LiveAttributionByLive }) {
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return live.products
    return live.products.filter(
      (p) => p.title.toLowerCase().includes(q) || p.productId.toLowerCase().includes(q),
    )
  }, [live.products, search])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Panel header */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--ink-100, #e5e7eb)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--ink-500, #6b7280)' }}>
            Catálogo da live
          </span>
          <span style={{ fontSize: 12, color: 'var(--ink-400, #9ca3af)', fontWeight: 600 }}>
            {live.products.length} produto{live.products.length !== 1 ? 's' : ''}
          </span>
        </div>
        <input
          type="search"
          placeholder="Buscar produto..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            marginTop: 8,
            width: '100%',
            padding: '6px 10px',
            fontSize: 12,
            border: '1px solid var(--ink-200, #e5e7eb)',
            borderRadius: 6,
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Product list */}
      <div style={{ flex: 1, overflowY: 'auto', maxHeight: 480 }}>
        {filtered.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--ink-300, #9ca3af)', textAlign: 'center' }}>
            Nenhum produto encontrado.
          </div>
        )}
        {filtered.map((p) => (
          <div
            key={p.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 16px',
              borderBottom: '1px solid var(--ink-50, #f3f4f6)',
            }}
          >
            {/* Thumbnail square */}
            <div style={{
              width: 32,
              height: 32,
              borderRadius: 6,
              background: productColor(p.title),
              color: '#fff',
              fontWeight: 700,
              fontSize: 11,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              {initials(p.title)}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontSize: 13,
                color: 'var(--ink-800, #111827)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {p.title}
              </div>
              <div style={{ fontSize: 10, color: 'var(--ink-300, #9ca3af)', fontFamily: 'var(--font-mono, monospace)' }}>
                {p.productId}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── OrdersPanel ─────────────────────────────────────────────────────────────

type OrderFilter = 'all' | 'matched' | 'unmatched'

const PAGE_SIZE = 30

function OrdersPanel({ live }: { live: LiveAttributionByLive }) {
  const [filter, setFilter] = useState<OrderFilter>('all')
  const [page, setPage] = useState(1)

  const matchedCount = live.audienceTransactions.filter((tx) => tx.matched).length
  const unmatchedCount = live.audienceTransactions.length - matchedCount

  const filtered = useMemo(() => {
    if (filter === 'matched') return live.audienceTransactions.filter((tx) => tx.matched)
    if (filter === 'unmatched') return live.audienceTransactions.filter((tx) => !tx.matched)
    return live.audienceTransactions
  }, [live.audienceTransactions, filter])

  const visible = filtered.slice(0, page * PAGE_SIZE)
  const hasMore = visible.length < filtered.length

  const handleFilter = (f: OrderFilter) => {
    setFilter(f)
    setPage(1)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Panel header */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--ink-100, #e5e7eb)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--ink-500, #6b7280)' }}>
            Pedidos do público
          </span>
          <span style={{ fontSize: 12, color: 'var(--ink-400, #9ca3af)' }}>
            {live.audienceTransactions.length} pedido{live.audienceTransactions.length !== 1 ? 's' : ''}
            {' · '}{matchedCount} com match GA
          </span>
        </div>
        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4 }}>
          <TabBtn active={filter === 'all'} onClick={() => handleFilter('all')}>
            Todos
          </TabBtn>
          <TabBtn active={filter === 'matched'} onClick={() => handleFilter('matched')}>
            <span style={{
              display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
              background: 'var(--success, #16a34a)', marginRight: 4,
            }} />
            Match GA
          </TabBtn>
          <TabBtn active={filter === 'unmatched'} onClick={() => handleFilter('unmatched')}>
            <span style={{
              display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
              background: 'var(--warning, #d97706)', marginRight: 4,
            }} />
            Sem match
          </TabBtn>
        </div>
      </div>

      {/* Order list */}
      <div style={{ flex: 1, overflowY: 'auto', maxHeight: 480 }}>
        {filtered.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--ink-300, #9ca3af)', textAlign: 'center' }}>
            Nenhum pedido com esse filtro.
          </div>
        )}
        {visible.map((tx) => (
          <OrderRow key={tx.transactionId} tx={tx} />
        ))}
        {hasMore && (
          <button
            type="button"
            onClick={() => setPage((p) => p + 1)}
            style={{
              width: '100%',
              padding: '10px',
              fontSize: 12,
              background: 'var(--ink-50, #f9fafb)',
              border: 'none',
              borderTop: '1px solid var(--ink-100, #e5e7eb)',
              cursor: 'pointer',
              color: 'var(--ink-600, #374151)',
            }}
          >
            Carregar mais ({filtered.length - visible.length} restantes)
          </button>
        )}
      </div>

      {/* Summary row */}
      <div style={{
        padding: '8px 16px',
        borderTop: '1px solid var(--ink-100, #e5e7eb)',
        background: 'var(--ink-50, #f9fafb)',
        display: 'flex',
        gap: 16,
        fontSize: 11,
        color: 'var(--ink-400, #9ca3af)',
      }}>
        <span><strong style={{ color: 'var(--success, #16a34a)' }}>{matchedCount}</strong> match GA</span>
        <span><strong style={{ color: 'var(--warning, #d97706)' }}>{unmatchedCount}</strong> sem match</span>
      </div>
    </div>
  )
}

function OrderRow({ tx }: { tx: LiveAudienceTransaction }) {
  const [open, setOpen] = useState(false)
  const totalRevenue = tx.items.reduce((s, it) => s + (it.itemRevenue || 0), 0)
  const displayName = tx.leadName || tx.leadContact
  const avatarLabel = displayName ? initials(displayName) : '?'

  return (
    <div
      style={{
        borderBottom: '1px solid var(--ink-50, #f3f4f6)',
        background: tx.matched ? 'var(--success-bg, #f0fdf4)' : undefined,
      }}
    >
      <div
        onClick={() => setOpen((v) => !v)}
        style={{ padding: '9px 16px', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 10 }}
      >
        {/* Avatar */}
        <div style={{
          width: 30,
          height: 30,
          borderRadius: '50%',
          background: tx.matched ? 'var(--success, #16a34a)' : 'var(--ink-200, #d1d5db)',
          color: '#fff',
          fontWeight: 700,
          fontSize: 11,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          {avatarLabel}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Name / uid */}
          {displayName ? (
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-800, #111827)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {tx.leadName}
              {tx.leadName && tx.leadContact && <span style={{ color: 'var(--ink-300, #9ca3af)' }}> · </span>}
              {tx.leadContact}
            </div>
          ) : (
            <div style={{ fontSize: 11, color: 'var(--ink-300, #9ca3af)', fontFamily: 'var(--font-mono, monospace)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {tx.widdeUid}
            </div>
          )}

          {/* Time + badges + revenue */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 2, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: 'var(--ink-400, #6b7280)' }}>
              {formatDateHourMinute(tx.dateHourMinute)}
            </span>
            <span style={{
              fontSize: 10,
              fontWeight: 600,
              padding: '1px 6px',
              borderRadius: 10,
              background: tx.matched ? 'var(--success-bg, #dcfce7)' : 'var(--warning-bg, #fef9c3)',
              color: tx.matched ? 'var(--success, #16a34a)' : 'var(--warning, #d97706)',
            }}>
              {tx.matched ? 'matched' : 'sem match'}
            </span>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-700, #374151)', marginLeft: 'auto' }}>
              {formatCurrency(totalRevenue)}
            </span>
          </div>

          {/* Matched item chips */}
          {tx.matchedItemNames.length > 0 && (
            <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
              {tx.matchedItemNames.map((name, i) => (
                <span key={i} style={{
                  fontSize: 10,
                  background: 'var(--success-bg, #dcfce7)',
                  color: 'var(--success, #166534)',
                  border: '1px solid var(--success, #86efac)',
                  padding: '1px 6px',
                  borderRadius: 8,
                }}>
                  {name}
                </span>
              ))}
            </div>
          )}
        </div>

        <span style={{ fontSize: 10, color: 'var(--ink-300, #9ca3af)', marginTop: 2 }}>{open ? '▾' : '▸'}</span>
      </div>

      {/* Expanded: all items */}
      {open && (
        <div style={{ padding: '4px 16px 10px 56px', borderTop: '1px dashed var(--ink-100, #e5e7eb)' }}>
          {tx.items.map((it, idx) => {
            const isMatched = tx.matchedItemNames.includes(it.itemName)
            return (
              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '3px 0' }}>
                <span style={{ color: isMatched ? 'var(--success, #16a34a)' : 'var(--ink-600, #374151)' }}>
                  {isMatched && '✓ '}{it.itemName}
                </span>
                <span style={{ color: 'var(--ink-400, #9ca3af)' }}>{formatCurrency(it.itemRevenue || 0)}</span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── AudiencePanel ────────────────────────────────────────────────────────────

function AudiencePanel({ live }: { live: LiveAttributionByLive }) {
  const total = live.audienceTransactions.length
  const matched = live.audienceTransactions.filter((tx) => tx.matched).length
  const unmatched = total - matched
  const matchPct = total > 0 ? Math.round((matched / total) * 100) : 0

  const identified = live.audienceTransactions.filter(
    (tx) => tx.matched && (tx.leadName || tx.leadContact),
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Panel header */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--ink-100, #e5e7eb)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--ink-500, #6b7280)' }}>
            Público atribuído
          </span>
          <span style={{ fontSize: 12, color: 'var(--ink-400, #9ca3af)' }}>
            {matched} identificado{matched !== 1 ? 's' : ''} · {unmatched} anônimo{unmatched !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      <div style={{ padding: 16, flex: 1, overflowY: 'auto' }}>
        {/* Donut chart */}
        {total > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
            <DonutChart matched={matched} total={total} />
            <div>
              {/* Match rate card */}
              <div style={{
                background: 'var(--success-bg, #f0fdf4)',
                border: '1px solid #86efac',
                borderRadius: 8,
                padding: '10px 14px',
              }}>
                <div style={{ fontSize: 11, color: 'var(--success, #16a34a)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Taxa de match GA
                </div>
                <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--success, #16a34a)', lineHeight: 1.1 }}>
                  {matchPct}%
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink-400, #6b7280)', marginTop: 2 }}>
                  {matched} de {total} pedido{total !== 1 ? 's' : ''}
                </div>
              </div>
            </div>
          </div>
        )}

        {total === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--ink-300, #9ca3af)', textAlign: 'center' }}>
            Nenhum pedido no público desta live.
          </div>
        )}

        {/* Identified buyers */}
        {identified.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--ink-400, #6b7280)', marginBottom: 8 }}>
              Compradores identificados
            </div>
            {identified.map((tx) => {
              const displayName = tx.leadName || tx.leadContact || ''
              return (
                <div key={tx.transactionId} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 0',
                  borderBottom: '1px solid var(--ink-50, #f3f4f6)',
                }}>
                  <div style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: 'var(--success, #16a34a)',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {initials(displayName)}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-800, #111827)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {tx.leadName}
                      {tx.leadName && tx.leadContact && <span style={{ color: 'var(--ink-300, #9ca3af)' }}> · </span>}
                      {tx.leadContact}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--ink-400, #6b7280)' }}>
                      {formatDateHourMinute(tx.dateHourMinute)}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Unmatched warning */}
        {unmatched > 0 && (
          <div style={{
            background: 'var(--warning-bg, #fef9c3)',
            border: '1px solid #fde047',
            borderRadius: 8,
            padding: '10px 12px',
            fontSize: 12,
            color: 'var(--warning, #d97706)',
          }}>
            <strong>{unmatched} pedido{unmatched !== 1 ? 's' : ''} sem match no GA</strong>
            <div style={{ fontSize: 11, marginTop: 2, color: 'var(--ink-400, #6b7280)' }}>
              Pedidos dentro da janela de atribuição não associados a um usuário identificado no GA.
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function DonutChart({ matched, total }: { matched: number; total: number }) {
  const r = 36
  const cx = 44
  const cy = 44
  const circumference = 2 * Math.PI * r
  const pct = total > 0 ? matched / total : 0
  const dash = pct * circumference
  const gap = circumference - dash

  return (
    <svg width={88} height={88} viewBox="0 0 88 88">
      {/* Background ring */}
      <circle
        cx={cx} cy={cy} r={r}
        fill="none"
        stroke="var(--ink-100, #e5e7eb)"
        strokeWidth={10}
      />
      {/* Matched arc */}
      <circle
        cx={cx} cy={cy} r={r}
        fill="none"
        stroke="var(--success, #16a34a)"
        strokeWidth={10}
        strokeDasharray={`${dash} ${gap}`}
        strokeLinecap="round"
        transform={`rotate(-90 ${cx} ${cy})`}
      />
      {/* Center label */}
      <text
        x={cx} y={cy}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={14}
        fontWeight={700}
        fill="var(--ink-800, #111827)"
      >
        {Math.round(pct * 100)}%
      </text>
    </svg>
  )
}

// ─── Shared UI atoms ──────────────────────────────────────────────────────────

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '3px 10px',
        fontSize: 11,
        borderRadius: 20,
        fontWeight: 600,
        background: active ? 'var(--accent-coral, #ff7a59)' : 'var(--ink-50, #f3f4f6)',
        color: active ? '#fff' : 'var(--ink-600, #374151)',
        border: active ? '1px solid var(--accent-coral, #ff7a59)' : '1px solid var(--ink-200, #e5e7eb)',
        cursor: 'pointer',
        transition: 'background 120ms, color 120ms',
      }}
    >
      {children}
    </button>
  )
}

